import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'

import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { ModalGrid } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'

const FORM_ID = 'create-table-options'

const tableTypes = [
  {
    id: 'table',
    title: 'Table',
    icon: <MaterialIcon icon='database' size={24} />,
    description:
      'The foundation of your data. A regular table with all the core features. Best for simple applications and general content. You always start with a table, then build snapshots and queries from it.',
    badge: 'Core',
  },
  {
    id: 'snapshot',
    title: 'Snapshot',
    icon: <MaterialIcon icon='photo_camera' size={24} />,
    description:
      "A read-only copy of a table, optimized for performance. Perfect for large or rarely changing data (e.g., past bookings, archived articles). Use when users don't need to edit or update the data.",
    badge: 'Read-only',
  },
  {
    id: 'query',
    title: 'Query',
    icon: <MaterialIcon icon='database_search' size={24} />,
    description:
      'A filtered, optimized view for calculations on smaller datasets. Use when you need performance and flexibility—calculations, updates, deletes—without overloading your main table. Keep queries small and focused.',
    badge: 'Advanced',
  },
]

/**
 * this is the create table modal view
 *
 * @todo:
 *  use logic from DbClientContext and GridV2Context only
 *
 */
export function CreateTableOptionsModal() {
  const { t } = useTranslation()

  const [selectedType, setSelectedType] = useState(null)

  const {
    dbClient,
    showCreateTableOptions,
    setShowCreateTableOptions,
    setShowCreateTable,
    setShowSnapshotTable,
    setShowQueryTable,
  } = useDbClientContext()

  const [hasError, setHasError] = useBoolean(true)

  const [isBtnLoading, setIsBtnLoading] = useBoolean(false)

  useEffect(() => {
    if (selectedType) {
      setHasError(false)
    }
  }, [selectedType, setHasError])

  const handleSubmit = async () => {
    if (!dbClient || isBtnLoading) return

    setIsBtnLoading(true)
    if (selectedType === 'table') {
      setShowCreateTable(true)
    } else if (selectedType === 'snapshot') {
      setShowSnapshotTable('create')
    } else if (selectedType === 'query') {
      setShowQueryTable('create')
    }
    setShowCreateTableOptions(false)
    setIsBtnLoading(false)
  }

  return (
    <ModalGrid
      id={FORM_ID}
      show={showCreateTableOptions}
      onHide={() => {
        if (isBtnLoading) return
        setShowCreateTableOptions(false)
      }}
      onCancel={() => {
        if (isBtnLoading) return
        setShowCreateTableOptions(false)
      }}
      title={t('Add New Table')}
      isLoading={isBtnLoading}
      disabled={hasError}
      primaryTrailing={t('Create')}
      onConfirm={handleSubmit}
    >
      <div className='p-6 space-y-4'>
        {tableTypes.map((type) => {
          const isSelected = selectedType === type.id

          return (
            <button
              key={type.id}
              onClick={() => setSelectedType(type.id as any)}
              className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 hover:scale-[1.02] ${
                isSelected
                  ? 'border-teal-500  bg-teal-50 dark:bg-teal-500/10 shadow-lg shadow-teal-500/10 dark:shadow-teal-500/20'
                  : 'border-gray-600  bg-gray-50 dark:bg-gray-700/50 hover:border-gray-300 dark:hover:border-gray-500'
              }`}
            >
              <div className='flex items-start gap-4'>
                {type.icon}

                {/* Content */}
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-3 mb-2'>
                    <h3 className={`text-lg font-semibold dark:text-white text-gray-900`}>{type.title}</h3>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        type.id === 'table'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                          : type.id === 'snapshot'
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                            : 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                      }`}
                    >
                      {type.badge}
                    </span>
                  </div>
                  <p className={`text-sm leading-relaxed dark:text-gray-300 text-gray-600`}>{type.description}</p>
                </div>

                {/* Selection Indicator */}
                {isSelected && (
                  <div className='flex-shrink-0'>
                    <div className='w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center'>
                      <MaterialIcon icon='check' size={16} className='text-white' />
                    </div>
                  </div>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </ModalGrid>
  )
}
